import React, { useEffect, useRef, useState } from "react";
import "../styles/Spinner.css";

const base = process.env.PUBLIC_URL || '';

type ModeSpinnerProps = {
   options: string[];
   spinTrigger: number;
   spinDuration?: number;
   extraSpins?: number;
   delay?: number;
};

export function ModeSpinner({
                               options,
                               spinTrigger,
                               spinDuration = 3000,
                               extraSpins = 10,
                               delay = 0,
                            }: ModeSpinnerProps) {
   const containerRef = useRef<HTMLDivElement>(null);
   const animationRef = useRef<number | null>(null);
   const startTimeRef = useRef<number>(0);
   const speedRef = useRef<number>(0);
   const positionRef = useRef<number>(0);

   const itemHeight = 80;

   // Initialize items randomly, static at first
   const [items, setItems] = useState<string[]>(() => {
      if (!options || options.length === 0) return [];
      const shuffled = [...options];
      const randomIndex = Math.floor(Math.random() * options.length);
      while (shuffled[0] !== options[randomIndex]) {
         shuffled.push(shuffled.shift()!);
      }
      return shuffled;
   });

   const animate = (time: number) => {
      if (!startTimeRef.current) startTimeRef.current = time;
      const elapsed = time - startTimeRef.current;

      const distance = elapsed * speedRef.current;
      let newPos = positionRef.current + distance;

      while (newPos >= itemHeight) {
         newPos -= itemHeight;
         setItems(prev => [...prev.slice(1), prev[0]]);
      }

      if (containerRef.current) {
         containerRef.current.style.transform = `translateY(-${newPos}px)`;
      }

      positionRef.current = newPos;

      if (elapsed < spinDuration + (delay || 0)) {
         animationRef.current = requestAnimationFrame(animate);
      } else {
         if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
         const finalIndex = Math.floor(Math.random() * options.length);
         setItems(prev => {
            const rotated = [...prev];
            while (rotated[0] !== options[finalIndex]) {
               rotated.push(rotated.shift()!);
            }
            return rotated;
         });
         positionRef.current = 0;
         if (containerRef.current) {
            containerRef.current.style.transform = `translateY(0px)`;
         }
      }
   };

   useEffect(() => {
      // Only run spin when spinTrigger changes, never on first mount
      if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);

      positionRef.current = 0;
      startTimeRef.current = 0;

      const totalDistance = options.length * itemHeight * (extraSpins || 10);
      speedRef.current = totalDistance / spinDuration;

      animationRef.current = requestAnimationFrame(animate);

      return () => {
         if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [spinTrigger]);

   return (
      <div className="slot-machine-container-wrapper">
         <div className="slot-machine-container">
            <div className="spinner-window">
               <div className="spinner-list" ref={containerRef}>
                  {items.map((item, idx) => (
                     <div key={idx} className="spinner-item" style={{ height: `${itemHeight}px` }}>
                        {item}
                     </div>
                  ))}
               </div>
            </div>
            <img
               src={`${base}/images/rmt/slot-machine.png`}
               alt="Slot Machine Overlay"
               className="slot-machine-overlay"
            />
         </div>
      </div>
   );
}